// Quality Signals — Common Threads explorer (beta). Read-only: cluster/slice
// kb_signals along a chosen field, drill into a cluster's signals. No status
// workflow. The slice ("cluster by") is computed server-side from the metadata
// contract; "Similarity" (semantic clustering) is a follow-up.

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { SignalsTabs } from '../components/signals/SignalsTabs';
import {
  signalsThreadsApi,
  type ClusterBy,
  type Thread,
  type ThreadSignal,
  type ThreadStats,
  type ThreadsParams,
} from '../services/signalsThreads';
import styles from './SignalsThreadsPage.module.css';

function describeLoadError(err: unknown, what: string): string {
  const status = (err as { response?: { status?: number; data?: { detail?: string } } })?.response?.status;
  const detail = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail;
  if (status === 401 || status === 403) {
    return `You need admin access to view ${what}.`;
  }
  if (status != null) {
    return `Could not load ${what} — the server returned HTTP ${status}${detail ? `: ${detail}` : ''}.`;
  }
  return `Could not reach the server to load ${what}.`;
}

// Some writers stored a stringified list in the sku metadata ("['A', 'A']");
// render its distinct values as individual chips instead of the raw repr.
function skuChips(skus: string[]): string[] {
  const out: string[] = [];
  for (const raw of skus) {
    const m = raw.match(/^\[.*\]$/s);
    const parts = m
      ? Array.from(raw.matchAll(/'([^']+)'|"([^"]+)"/g)).map((g) => g[1] ?? g[2])
      : [raw];
    for (const v of parts) {
      const t = v.trim();
      if (t && !out.includes(t)) out.push(t);
    }
  }
  return out;
}

const STALE_AFTER_DAYS = 180;

const SEVERITY_COLOR: Record<Thread['severity'], string> = {
  critical: '#e03131',
  high: '#f76707',
  watch: '#74b816',
};

// The slices a user can pick. "Recurring threads" is the default customer +
// complaint-type view; the rest re-cluster the same signals by one field.
const SLICES: { key: ClusterBy; label: string }[] = [
  { key: 'thread', label: 'Recurring threads' },
  { key: 'customer', label: 'Customer' },
  { key: 'sku', label: 'SKU' },
  { key: 'theme', label: 'Complaint type' },
  { key: 'signal_type', label: 'Signal type' },
  { key: 'source_channel', label: 'Channel' },
  { key: 'status', label: 'Status' },
];

function ordinal(n: number): string {
  if (n >= 4) return '4th+ time';
  if (n === 3) return '3rd time';
  if (n === 2) return '2nd time';
  return '1st';
}

function fmtWhen(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return (
    d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) +
    ' · ' +
    d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })
  );
}

export function SignalsThreadsPage() {
  const [threads, setThreads] = useState<Thread[]>([]);
  const [stats, setStats] = useState<ThreadStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [customer, setCustomer] = useState('');
  const [theme, setTheme] = useState('');
  const [sku, setSku] = useState('');
  const [days, setDays] = useState('');
  const [clusterBy, setClusterBy] = useState<ClusterBy>('thread');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const navigate = useNavigate();

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params: ThreadsParams = { cluster_by: clusterBy };
      if (customer) params.customer = customer;
      if (theme) params.theme = theme;
      if (sku) params.sku = sku;
      if (days) params.days = Number(days);
      const res = await signalsThreadsApi.list(params);
      setThreads(res.threads);
      setStats(res.stats);
    } catch (err) {
      setError(describeLoadError(err, 'threads'));
    } finally {
      setLoading(false);
    }
  }, [clusterBy, customer, theme, sku, days]);

  useEffect(() => {
    const t = setTimeout(load, 150);
    return () => clearTimeout(t);
  }, [load]);

  // Filter options come from the cluster rollups so they're meaningful for any
  // slice (a SKU cluster still knows its customers/themes).
  const customerOptions = useMemo(
    () => Array.from(new Set(threads.flatMap((t) => t.customers))).sort(),
    [threads],
  );
  const themeOptions = useMemo(
    () => Array.from(new Set(threads.flatMap((t) => t.themes))).sort(),
    [threads],
  );
  const skuOptions = useMemo(
    () => Array.from(new Set(threads.flatMap((t) => skuChips(t.skus)))).sort(),
    [threads],
  );

  const reset = () => {
    setCustomer('');
    setTheme('');
    setSku('');
    setDays('');
  };

  const selected = threads.find((t) => t.id === selectedId) ?? null;

  // "Recurring threads" keeps the two-level by-customer view; every other slice
  // is a flat, count-ranked list of clusters.
  const grouped = useMemo((): Array<{ header: string | null; pill: string; items: Thread[] }> => {
    const sorted = [...threads].sort((a, b) => b.count - a.count);
    if (clusterBy === 'thread') {
      const by = new Map<string, Thread[]>();
      for (const t of sorted) by.set(t.customer, [...(by.get(t.customer) ?? []), t]);
      return Array.from(by.entries())
        .sort((a, b) => a[0].localeCompare(b[0]))
        .map(([name, items]) => ({
          header: name,
          pill: `${items.reduce((s, x) => s + x.count, 0)} signals`,
          items,
        }));
    }
    const total = sorted.reduce((s, x) => s + x.count, 0);
    return [{ header: null, pill: `${sorted.length} clusters · ${total} signals`, items: sorted }];
  }, [threads, clusterBy]);

  const clusterNoun = clusterBy === 'thread' ? 'threads' : 'clusters';

  return (
    <div className={styles.page}>
      <SignalsTabs />

      <header className={styles.head}>
        <h1>Common threads <span className={styles.beta}>beta</span></h1>
        <p>
          Recurring signal clusters from kb_signals — slice by customer,
          complaint type, SKU, signal type, channel or status. Read-only.
        </p>
      </header>

      {stats && (
        <div className={styles.stats}>
          <Stat n={stats.signals} label="Signals in view" sub={`across ${stats.threads} ${clusterNoun}`} />
          <Stat n={stats.customers} label="Customers affected" sub="with at least one signal" />
          <Stat n={stats.recurring} label="Recurring patterns" sub="3+ signals in one cluster" alert={stats.recurring > 0} />
          <Stat n={stats.top_customer ?? '—'} label="Most active customer" sub="by signal count" />
        </div>
      )}

      <div className={styles.sliceBar}>
        <span className={styles.sliceLabel}>Cluster by</span>
        <div className={styles.slices}>
          {SLICES.map((s) => (
            <button
              key={s.key}
              type="button"
              className={clusterBy === s.key ? styles.on : ''}
              onClick={() => { setClusterBy(s.key); setSelectedId(null); }}
            >
              {s.label}
            </button>
          ))}
          <button
            type="button"
            className={styles.similarity}
            onClick={() => navigate('/signals/constellations')}
            title="Semantic clustering — named clusters sized by frequency, over a time window"
          >
            Similarity → Constellations
          </button>
        </div>
      </div>

      <p className={styles.soonNote}>
        Looking for semantic &ldquo;similar complaints&rdquo; clustering? It now lives
        in <button type="button" className={styles.inlineLink} onClick={() => navigate('/signals/constellations')}>Constellations</button> —
        named clusters along each signal dimension, sized by frequency, over a time window.
      </p>

      <div className={styles.filters}>
        <select value={customer} onChange={(e) => setCustomer(e.target.value)}>
          <option value="">All customers</option>
          {customerOptions.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
        <select value={theme} onChange={(e) => setTheme(e.target.value)}>
          <option value="">All types</option>
          {themeOptions.map((t) => <option key={t} value={t}>{t}</option>)}
        </select>
        <select value={sku} onChange={(e) => setSku(e.target.value)}>
          <option value="">All SKUs</option>
          {skuOptions.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        <select value={days} onChange={(e) => setDays(e.target.value)}>
          <option value="">All time</option>
          <option value="7">Last 7 days</option>
          <option value="14">Last 14 days</option>
          <option value="30">Last 30 days</option>
        </select>
        <button type="button" className={styles.reset} onClick={reset}>Reset</button>
      </div>

      {loading && <div className={styles.muted}>Loading…</div>}
      {error && <div className={styles.error}>{error}</div>}
      {!loading && !error && threads.length === 0 && (
        <div className={styles.muted}>
          No clusters yet — they appear here once signals accumulate in kb_signals.
        </div>
      )}

      {!loading && !error && threads.length > 0 && (
        <div className={styles.split}>
          <div className={styles.list}>
            {grouped.map((group, gi) => (
              <div key={group.header ?? `g${gi}`}>
                <div className={styles.groupH}>
                  {group.header ?? `${SLICES.find((s) => s.key === clusterBy)?.label ?? 'Clusters'}`}
                  <span className={styles.pill}>{group.pill}</span>
                </div>
                {group.items.map((t) => (
                  <ThreadCard
                    key={t.id}
                    thread={t}
                    selected={t.id === selectedId}
                    onClick={() => setSelectedId(t.id)}
                  />
                ))}
              </div>
            ))}
          </div>
          <div className={styles.detail}>
            {selected ? <Drilldown thread={selected} /> : (
              <div className={styles.placeholder}>← Pick a cluster to see its signals.</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function Stat({ n, label, sub, alert }: { n: number | string; label: string; sub: string; alert?: boolean }) {
  return (
    <div className={`${styles.stat} ${alert ? styles.statAlert : ''}`}>
      <div className={styles.statN}>{n}</div>
      <div className={styles.statL}>{label}</div>
      <div className={styles.statD}>{sub}</div>
    </div>
  );
}

function ThreadCard({ thread, selected, onClick }: { thread: Thread; selected: boolean; onClick: () => void }) {
  const isThread = thread.cluster_by === 'thread';
  // tag rollups: themes + skus, plus signal types when that's the lens
  const extraTags = thread.cluster_by === 'signal_type' || thread.cluster_by === 'status'
    ? thread.signal_types.slice(0, 2)
    : [];
  return (
    <div className={`${styles.thread} ${selected ? styles.threadSel : ''}`} onClick={onClick}>
      <div className={styles.title}>
        <span className={styles.dot} style={{ background: SEVERITY_COLOR[thread.severity] }} />
        {thread.title}
      </div>
      <div className={styles.cust}>{thread.customer}</div>
      <div className={styles.row2}>
        <span className={styles.count}>{thread.count} signal{thread.count > 1 ? 's' : ''}</span>
        <span className={`${styles.recur} ${thread.last_seen_days != null && thread.last_seen_days > STALE_AFTER_DAYS ? styles.stale : ''}`}>
          {isThread ? `occurrence: ${ordinal(thread.count)}` : `${thread.customers.length || 1} customer${thread.customers.length === 1 ? '' : 's'}`}
          {thread.last_seen_days != null ? ` · last ${thread.last_seen_days}d ago` : ''}
          {thread.last_seen_days != null && thread.last_seen_days > STALE_AFTER_DAYS ? ' · stale' : ''}
        </span>
      </div>
      <div className={styles.tags}>
        {thread.themes.slice(0, 4).map((t) => <span key={t} className={styles.tag}>{t}</span>)}
        {skuChips(thread.skus).map((s) => <span key={s} className={`${styles.tag} ${styles.sku}`}>{s}</span>)}
        {extraTags.map((s) => <span key={s} className={styles.tag}>{s}</span>)}
      </div>
    </div>
  );
}

function Drilldown({ thread }: { thread: Thread }) {
  const isThread = thread.cluster_by === 'thread';
  return (
    <div>
      <div className={styles.dh}>
        <div className={styles.dt}>{thread.title}</div>
        <div className={styles.dc}>
          {thread.customer} · {thread.count} signals
          {isThread ? ` · occurrence #${thread.count}` : ''}
        </div>
      </div>
      <div className={styles.body}>
        {thread.members.map((m: ThreadSignal) => (
          <div key={m.id} className={styles.sig}>
            <div className={styles.when}>
              {fmtWhen(m.observed_at)} · {m.source_channel} · {m.observed_by}
            </div>
            <div className={styles.obs}>{m.observation}</div>
            {m.significance && (
              <div className={styles.so}><b>So what:</b> {m.significance}</div>
            )}
            <div className={styles.chips}>
              <span className={styles.chip}><b>type:</b> {m.signal_type}</span>
              <span className={styles.chip}><b>status:</b> {m.status}</span>
              {m.customer_no && <span className={styles.chip}><b>cust:</b> {m.customer_no}</span>}
              {m.sku && <span className={styles.chip}><b>sku:</b> {skuChips([m.sku]).join(', ')}</span>}
              {m.batch_id && <span className={styles.chip}><b>batch:</b> {m.batch_id}</span>}
              {m.order_id && <span className={styles.chip}><b>order:</b> {m.order_id}</span>}
              <span className={styles.chip}><b>conf:</b> {m.confidence}%</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
